import { action, computed, observable, runInAction } from "mobx";
import qs from "qs";

import BrowseStore from "./browse";
import CommonStore from "./common";
import { getQuery } from "../service/http";
import { goLogin } from "../service/utils";

const isDev =
  typeof window !== "undefined" && window.location.hostname === "localhost";

const EMPTY_FILTER_PARAMS = {
  order_by: [],
  tags: [],
  cuisines_categories: [],
  price_range: [],
  distance: [],
  locations: [],
  landmarks: [],
  cuisines: [],
  extras: [],
};

function normalizeArray(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((v) => v != null && v !== "");
}

function arrayEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function paramsBuilder(params) {
  const nextParams = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      if (value.length > 0) nextParams[key] = value.join(",");
      return;
    }
    if (value === "" || value == null) return;
    nextParams[key] = value;
  });
  return nextParams;
}

class Store {
  history = null;
  _totalFetchTimer = null;
  _totalLoadingTimer = null;
  _totalRequestSeq = 0;
  _initialized = false;

  // Router snapshot (drives activeTab + URL sync)
  @observable pathname = "";

  // Query-derived base fields
  @observable region = "";
  @observable lang = "";
  @observable region_id = "";
  @observable regionId = "";
  @observable private_token = "";

  // Browse filter state (extracted from pages/browse)
  @observable state = {
    sort: "",
    distance: "",
    "location.id": [],
    "landmarks.id": [],
    "tags.id": [],
    "cuisines.id": [],
    "extras.id": [],
    avg_price: "",
  };

  // Head-bar search state
  @observable searchState = {
    seats: "",
    date: "",
    q: "",
    region: "",
    regionInfo: null,
  };

  @observable searchTrigger = 0;
  @observable exploreMapMode = false;

  // Shared region/city data
  @observable regionList = [];
  @observable currentCity = null;
  @observable pageCity = {};
  @observable searchRegionList = [];

  // Keep the old ref-like API surface for components.
  posRef = { current: null };

  // Filter parameters + derived counts
  @observable filterParams = EMPTY_FILTER_PARAMS;
  @observable paramsLoading = false;

  // Total count for "See X restaurants" in filters
  @observable loading = false;
  @observable total = 0;

  @computed
  get activeTab() {
    if (this.pathname === "/home") return "home";
    if (this.pathname === "/browse") return "explore";
    return this.pathname;
  }

  @computed
  get sums() {
    const location =
      normalizeArray(this.state["location.id"]).length +
      normalizeArray(this.state["landmarks.id"]).length +
      (this.state.distance ? 1 : 0);
    const cuisines =
      normalizeArray(this.state["cuisines.id"]).length +
      normalizeArray(this.state["extras.id"]).length;

    return {
      sort: this.state.sort && this.state.sort !== "default" ? 1 : 0,
      filters:
        location +
        cuisines +
        normalizeArray(this.state["tags.id"]).length +
        (this.state.avg_price ? 1 : 0),
      location,
      cuisines,
    };
  }

  @action.bound
  attachRouter(history) {
    this.history = history;
  }

  @action.bound
  syncFromLocation(routerLocation) {
    const urlSearch = getQuery();

    if (routerLocation?.pathname && this.pathname !== routerLocation.pathname) {
      this.pathname = routerLocation.pathname;
    }
    if (typeof document !== "undefined" && routerLocation?.pathname) {
      document.body.setAttribute("path", routerLocation.pathname);
    }

    const nextLang = urlSearch.lang || "";
    const nextRegion = urlSearch.region || "";
    const nextRegionId = urlSearch.regionId || "";
    const nextRegion_id = urlSearch.region_id || "";
    const nextToken = urlSearch.private_token || "";

    const prevRegion = this.region;

    const q = urlSearch.q == null ? "" : String(urlSearch.q);
    const seats = urlSearch.seats == null ? "" : String(urlSearch.seats);
    const date = urlSearch.date == null ? "" : String(urlSearch.date);

    // Query-derived top-level fields
    if (this.lang !== nextLang) this.lang = nextLang;
    if (this.region !== nextRegion) this.region = nextRegion;
    if (this.regionId !== nextRegionId) this.regionId = nextRegionId;
    if (this.region_id !== nextRegion_id) this.region_id = nextRegion_id;
    if (this.private_token !== nextToken) this.private_token = nextToken;

    // Each route change should reset searchState to reflect query params.
    this.setSearchState({
      seats,
      date,
      q,
      region: urlSearch.region || "",
    });

    if (prevRegion !== nextRegion) {
      this.cleanFilter();
      if (nextRegion) this.fetchPageCityByRegion(nextRegion);
      this.fetchFilterParams();
    }

    if (this.activeTab === "explore") {
      this.scheduleTotalFetch();
    }

    // Leaving /browse should reset map mode back to list.
    if (this.activeTab !== "explore" && this.exploreMapMode) {
      this.exploreMapMode = false;
    }
  }

  @action.bound
  init() {
    if (this._initialized) return;
    this._initialized = true;

    const urlSearch = getQuery();
    runInAction(() => {
      this.lang = urlSearch.lang || this.lang || "";
      this.region = urlSearch.region || this.region || "";
      this.regionId = urlSearch.regionId || this.regionId || "";
      this.region_id = urlSearch.region_id || this.region_id || "";
      this.private_token = urlSearch.private_token || this.private_token || "";
      this.searchState = {
        ...this.searchState,
        seats: urlSearch.seats == null ? "" : String(urlSearch.seats),
        date: urlSearch.date == null ? "" : String(urlSearch.date),
        q: urlSearch.q == null ? "" : String(urlSearch.q),
        region: urlSearch.region || "",
      };
    });

    if (this.region) this.fetchPageCityByRegion(this.region);
    this.getPosition();
    this.fetchRegions();
    this.fetchFilterParams();
    this.scheduleTotalFetch();
  }

  @action.bound
  setFilterLoading(loading) {
    this.loading = loading;
  }
  @action.bound
  setFilterTotal(total) {
    this.total = total;
  }

  @action.bound
  setExploreMapMode(next) {
    this.exploreMapMode = !!next;
  }

  @action.bound
  setPageCity(city) {
    this.pageCity = city || {};
  }

  @action.bound
  setState(updater) {
    const prev = this.state;
    const patch = typeof updater === "function" ? updater(prev) : updater;
    const nextPatch = patch || {};

    let changed = false;
    const next = { ...prev };
    Object.keys(nextPatch).forEach((key) => {
      const nextVal = nextPatch[key];
      const prevVal = prev[key];

      if (Array.isArray(nextVal) || Array.isArray(prevVal)) {
        if (arrayEqual(prevVal, nextVal)) return;
        next[key] = Array.isArray(nextVal) ? [...nextVal] : nextVal;
        changed = true;
        return;
      }

      if (prevVal === nextVal) return;
      next[key] = nextVal;
      changed = true;
    });

    if (!changed) return;
    this.state = next;
    this.scheduleTotalFetch();
  }

  @action.bound
  setSearchState(updater) {
    const prev = this.searchState;
    const patch = typeof updater === "function" ? updater(prev) : updater;
    const nextPatch = patch || {};

    let changed = false;
    const next = { ...prev };
    Object.keys(nextPatch).forEach((key) => {
      const nextVal = nextPatch[key];
      const prevVal = prev[key];
      if (prevVal === nextVal) return;
      next[key] = nextVal;
      changed = true;
    });

    if (!changed) return;
    this.searchState = next;
  }

  @action.bound
  bumpSearchTrigger() {
    this.searchTrigger += 1;
  }

  @action.bound
  cleanFilter() {
    this.state = {
      ...this.state,
      distance: "",
      "location.id": [],
      "landmarks.id": [],
      "tags.id": [],
      "cuisines.id": [],
      "extras.id": [],
      avg_price: "",
    };
    this.scheduleTotalFetch();
  }

  getSearch(params = {}) {
    const {
      region = "",
      lang = "",
      gid = "",
      private_token = "",
      callback = "",
      payment = "",
      login_url = "",
      source = "",
    } = getQuery();

    const obj = {
      gid,
      lang,
      region,
      private_token,
      callback,
      payment,
      login_url,
      source,
      ...params,
    };

    Object.keys(obj).forEach((key) => {
      if (obj[key] === "" || obj[key] == null) obj[key] = null;
    });

    return qs.stringify(obj, { skipNulls: true });
  }

  @action.bound
  onHomeClick() {
    const { lang, region } = getQuery();

    const pageCityDomain = this.pageCity?.domain;
    let base = pageCityDomain
      ? `https://www.${pageCityDomain}/`
      : document?.referrer;
    if (isDev) base = "http://localhost:5500/";

    this.setSearchState({
      seats: "",
      date: "",
      q: "",
      region: this.region,
      regionInfo: null,
    });

    if (this.history) this.history.push(`/home?${this.getSearch({})}`);

    if (typeof window !== "undefined" && window.top) {
      window.top.postMessage(
        {
          type: "other",
          params: {
            url: region && lang ? `${base}${lang}/${region}` : base,
            region,
            lang,
          },
        },
        "*"
      );
    }
  }

  @action.bound
  onExploreClick() {
    const pageCityDomain = this.pageCity?.domain;

    if (isDev) {
      if (this.history) this.history.push(`/browse?${this.getSearch({})}`);
      return;
    }

    const { lang, region } = getQuery();
    const base = pageCityDomain
      ? `https://www.${pageCityDomain}/`
      : document?.referrer;

    this.setSearchState({
      seats: getQuery().seats || "",
      date: getQuery().date || "",
      q: getQuery().q || "",
      region: getQuery().region || "shanghai",
      regionInfo: null,
    });

    window.top.location.href = `${base}${lang}/${region}/search_restaurants?utf8=1&keyword=&search_restaurant_id=`;
  }

  @action.bound
  onUserClick() {
    const pageCityDomain = this.pageCity?.domain;

    if (isDev) {
      if (this.history) this.history.push(`/profile?${this.getSearch({})}`);
      return;
    }

    const { lang, region } = getQuery();
    if (!this.private_token) return goLogin();

    const base = pageCityDomain
      ? `https://www.${pageCityDomain}/`
      : document?.referrer;
    const url = `${base}${lang}/members/bookings`;
    window.top.location.href = url;
    window.top.postMessage(
      {
        type: "other",
        params: { url, region, lang },
      },
      "*"
    );
  }

  @action.bound
  onCityClick(city) {
    this.setPageCity(city);
    const nextRegion = city?.key_word;
    if (nextRegion) this.region = nextRegion;
    const url = `https://www.${city.domain}/${this.lang}/${nextRegion}`;
    const query = getQuery();
    this.cleanFilter();
    if (this.activeTab === "explore") {
      this.history.push(
        `/browse?${this.getSearch({
          q: query.q,
          seats: query.seats,
          date: query.date,
          region: nextRegion,
        })}`
      );
      return window.top.postMessage(
        {
          type: "other",
          params: { url, region: nextRegion, lang: this.lang },
        },
        "*"
      );
    } else {
      window.top.postMessage(
        {
          type: "switch",
          params: { url, region: nextRegion, lang: this.lang },
        },
        "*"
      );
    }
  }

  @action.bound
  onSearchBtnClick(keyword, city) {
    // this.bumpSearchTrigger();
    const nextRegion = city || this.searchState.region || this.region;
    this.history.push(
      `/browse?${this.getSearch({
        q: keyword,
        seats: this.searchState.seats,
        date: this.searchState.date,
        region: nextRegion,
      })}`
    );
    let obj = {
      utf8: 1,
      q: keyword,
      seats: this.searchState.seats,
      date: this.searchState.date,
    };

    Object.keys(obj).forEach((key) => {
      if (obj[key] === "" || obj[key] == null) obj[key] = null;
    });

    window.top.postMessage({
      type: "other",
      params: {
        region: nextRegion,
        lang: this.lang,
        url: `${
          this.pageCity?.domain
            ? `https://www.${this.pageCity?.domain}/`
            : document?.referrer
        }${this.lang}/${nextRegion}/search_restaurants?${qs.stringify(obj, {
          skipNulls: true,
        })}`,
      },
    });
  }

  @action.bound
  searchRegion(keyword) {
    CommonStore.searchRegion(keyword).then((res) => {
      runInAction(() => {
        this.searchRegionList = res || [];
      });
    });
  }

  @action.bound
  getPosition() {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        try {
          localStorage.setItem("pos", JSON.stringify({ lat, lng }));
        } catch (e) {
          // ignore
        }
        this.posRef.current = { lat, lng };
        CommonStore.getCityByLocation(lat, lng).then((res) => {
          runInAction(() => {
            this.currentCity = res;
          });
        });
      },
      () => {},
      {
        timeout: 30000,
        enableHighAccuracy: true,
        maximumAge: 75000,
      }
    );
  }

  @action.bound
  fetchRegions() {
    CommonStore.getAllRegions().then((res) => {
      if (!res) return;
      const next = (res || []).map((item) => {
        const mapRegions = {};
        (item.regions || []).forEach((city) => {
          const char = city?.key_word?.[0]?.toLocaleUpperCase();
          if (!char) return;
          if (!mapRegions[char]) mapRegions[char] = [];
          mapRegions[char].push(city);
        });
        return { ...item, regions: mapRegions };
      });

      runInAction(() => {
        this.regionList = next;
      });
    });
  }

  @action.bound
  fetchPageCityByRegion(region) {
    if (!region) return;
    // NOTE: CommonStore.getCityByRegion caches the first cityDetail and may return
    // stale results after switching regions. Use BrowseStore.getCityByRegion here
    // to always reflect the selected region.
    BrowseStore.getCityByRegion(region).then((data) => {
      if (!data) return;
      runInAction(() => {
        this.pageCity = data;
      });
    });
  }

  @action.bound
  fetchFilterParams() {
    if (!this.lang || !this.region) return;
    this.paramsLoading = true;
    BrowseStore.getBrowseParams({
      lang: this.lang,
      region_id: this.region,
    })
      .then((data) => {
        runInAction(() => {
          this.filterParams = data || EMPTY_FILTER_PARAMS;
        });
      })
      .catch(() => {
        // ignore
      })
      .then(
        action(() => {
          this.paramsLoading = false;
        })
      );
  }

  @action.bound
  scheduleTotalFetch() {
    return;
    if (this._totalFetchTimer) clearTimeout(this._totalFetchTimer);
    if (this._totalLoadingTimer) clearTimeout(this._totalLoadingTimer);

    const requestSeq = (this._totalRequestSeq += 1);

    this._totalLoadingTimer = setTimeout(
      action(() => {
        // Guard against stale timers from previous schedules.
        if (this._totalRequestSeq !== requestSeq) return;
        this.loading = true;
      }),
      300
    );

    this._totalFetchTimer = setTimeout(() => {
      this.fetchTotalCount(requestSeq);
    }, 200);
  }

  @action.bound
  fetchTotalCount(requestSeq) {
    if (!this.lang || !this.region) {
      if (this._totalLoadingTimer) {
        clearTimeout(this._totalLoadingTimer);
        this._totalLoadingTimer = null;
      }
      this.total = 0;
      this.loading = false;
      return;
    }

    const seq = requestSeq || (this._totalRequestSeq += 1);

    const urlSearch = getQuery();

    const params = {
      lang: this.lang,
      region: this.region,
      "api-key": "cgecegcegcc",
      page: 1,
      per_page: 1,
      ...paramsBuilder(this.state),
      ...paramsBuilder({
        q: urlSearch.q,
        date: urlSearch.date,
        seats: urlSearch.seats,
      }),
    };

    const finalize = action(() => {
      // Only the latest scheduled request may mutate loading state.
      if (this._totalRequestSeq !== seq) return;
      if (this._totalLoadingTimer) {
        clearTimeout(this._totalLoadingTimer);
        this._totalLoadingTimer = null;
      }
      this.loading = false;
    });

    BrowseStore.getBrowseRestaurants(params)
      .then((res) => {
        runInAction(() => {
          if (this._totalRequestSeq !== seq) return;
          const raw = res?.headers?.total_count;
          const n = raw == null ? 0 : Number(raw);
          this.total = Number.isFinite(n) ? n : 0;
        });
      })
      .catch(() => {
        runInAction(() => {
          if (this._totalRequestSeq !== seq) return;
          this.total = 0;
        });
      })
      .then(finalize);
  }
}

export default new Store();
